import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Subscriber } from "@/types/subscription";
import { generateWhatsAppVerificationCode } from "@/hooks/subscription/whatsappVerification";

interface WhatsAppVerificationButtonProps {
  subscriber: Subscriber;
  isProcessing: boolean;
  renderAsMenuItem?: boolean;
}

export default function WhatsAppVerificationButton({
  subscriber,
  isProcessing,
  renderAsMenuItem = false,
}: WhatsAppVerificationButtonProps) {
  const openManualWhatsApp = (phoneNumber: string, code: string) => {
    const formattedNumber = phoneNumber.replace(/\D/g, '');
    const whatsappMessage = encodeURIComponent(
      `Hello! Your verification code for your account is: ${code}\n\nTo verify your WhatsApp number, please enter this code on the verification page.`
    );

    window.open(`https://wa.me/${formattedNumber}?text=${whatsappMessage}`, '_blank');
  };

  const handleWhatsAppVerification = async () => {
    if (!subscriber?.profile?.whatsapp_number) {
      toast.error("No WhatsApp number found for this user");
      return;
    }

    if (!subscriber.user_id) {
      toast.error("No user record found for this subscriber");
      return;
    }

    try {
      const phoneNumber = subscriber.profile.whatsapp_number;
      const providerId = null;

      const result = await generateWhatsAppVerificationCode(providerId, phoneNumber, {
        deliveryMode: 'automated',
        targetUserId: subscriber.user_id,
      });

      if (result && result.success) {
        if (result.delivery === 'automated') {
          toast.success(result.message || 'Verification code sent automatically via WhatsApp');
          return;
        }

        if (result.code) {
          const message = `Automated delivery unavailable. Manual backup code for ${phoneNumber}: ${result.code}`;
          toast.success(message);
          openManualWhatsApp(phoneNumber, result.code);
        } else {
          toast.success(result.message || 'WhatsApp verification code generated successfully');
        }
      } else {
        toast.error(result.message || "Failed to generate verification code");
      }
    } catch (error) {
      console.error("Error initiating WhatsApp verification:", error);
      toast.error("Error initiating WhatsApp verification");
    }
  };

  if (renderAsMenuItem) {
    return (
      <DropdownMenuItem
        onSelect={(event) => {
          event.preventDefault();
          if (!isProcessing) {
            void handleWhatsAppVerification();
          }
        }}
        disabled={isProcessing}
        className="text-[#25D366] focus:text-white focus:bg-[#25D366]"
      >
        <MessageSquare className="mr-2 h-4 w-4" />
        <span>{isProcessing ? "Processing..." : "Verify WhatsApp"}</span>
      </DropdownMenuItem>
    );
  }

  return (
    <Button
      onClick={handleWhatsAppVerification}
      disabled={isProcessing}
      className="text-[#25D366] focus:text-white focus:bg-[#25D366]"
      variant="ghost"
      size="sm"
      type="button"
    >
      <MessageSquare className="mr-2 h-4 w-4" />
      <span>{isProcessing ? "Processing..." : "Verify WhatsApp"}</span>
    </Button>
  );
}
